import { animate, animateChild, query, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { CrmConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { PickSourceProvider } from '@frontend/sports/option/pick/feature/tracking';
import { Store } from '@ngrx/store';
import { orderBy } from 'lodash-es';
import { Observable, combineLatest, filter, map, take } from 'rxjs';

import { RewardTokenType } from '../../../../../lib/tokens-base/token-base.models';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import LinearBetslipTrackingActions from '../../../tracking/state/linear-tracking.actions';
import { RewardTokensActions } from '../../actions';
import { IRewardToken, IRewardTokenInfo, RewardTokenContext } from '../../reward-tokens.model';
import { selectAllSelectedTokens, selectAllTokensAndEligibilityStateFactory, selectTokenForContextFactory } from '../../selectors';
import { byAccaPriority, isAccaBoostToken } from '../../services/reward-tokens.utils';
import { valueSelectors } from './token-boost-animations';

export enum RewardTokensContainerMode {
    Desktop = 'Desktop',
    Mobile = 'Mobile',
}

interface TokensContainerViewModel {
    crmTokens: IRewardTokenInfo[];
    accaBoostToken?: IRewardTokenInfo;
}

@Component({
    selector: 'bs-reward-tokens-container',
    templateUrl: './reward-tokens-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-tokens-container.component.scss'],
    animations: [
        trigger('open', [
            transition(desktopMode, [
                style({ transform: 'scaleY(0)' }),
                animate('400ms cubic-bezier(.77,.07,.97,.64)'),
                query(valueSelectors, [animateChild()], { optional: true }),
            ]),
            transition(mobileMode, [
                style({ transform: 'translateY(100%)' }),
                animate('400ms cubic-bezier(.77,.07,.97,.64)'),
                query(valueSelectors, [animateChild()], { optional: true }),
            ]),
        ]),
    ],
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
})
export class RewardTokensContainerComponent implements OnInit {
    @HostBinding('class') class = 'reward-tokens-container reward-tokens-module cards-module';

    @HostBinding('@open')
    get openAnimationMode(): string {
        return this.mode.toString();
    }

    @HostBinding('class.reward-drawer__container') get formCoupon(): boolean {
        return this.isRewardDrawerEnabled;
    }

    @Input() mode: RewardTokensContainerMode;
    @Input() tokenContext: RewardTokenContext;

    tokenInfos: IRewardTokenInfo[];
    isRewardDrawerEnabled = false;
    shouldScrollToContent = false;
    scrollHeight = 0;
    isEpcotEnabled = this.epcotConfig.isEnabled;
    isTokenSortingEnabled = false;
    isDoneCTAEnabled = false;
    isDoneButtonDisabled = true;

    model$: Observable<TokensContainerViewModel>;

    trackingSource?: { [key: string]: string };

    constructor(
        public sitecore: Sitecore,
        private store: Store<IBetslipRootState>,
        private epcotConfig: EpcotConfig,
        private tokensConfig: CrmConfig,
        private pickSourceProvider: PickSourceProvider,
    ) {}

    ngOnInit(): void {
        this.isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
        this.isDoneCTAEnabled = this.tokensConfig.tokens.showApplyRemoveButtons;
        this.isTokenSortingEnabled = this.tokensConfig.tokens.isTokenSortingEnabled;

        this.shouldScrollToContent = this.isRewardDrawerEnabled && this.mode === RewardTokensContainerMode.Desktop;

        const selectedToken$ = this.store.select(selectTokenForContextFactory(this.tokenContext));
        const tokensEligibility$ = this.store.select(selectAllTokensAndEligibilityStateFactory(this.tokenContext));
        const allSelectedTokens$ = this.store.select(selectAllSelectedTokens);

        this.model$ = combineLatest([tokensEligibility$, allSelectedTokens$, selectedToken$]).pipe(
            map(([{ tokens, eligibilityState }, allSelectedTokens, selectedToken]) => {
                const tokenList = eligibilityState
                    .filter((eligibility) => {
                        const currentSelected = selectedToken?.userTokenId === eligibility.tokenId;
                        // this makes sure that already selected tokens in a different slip don't get shown
                        const availableForSelection = allSelectedTokens.every((token) => token.userTokenId !== eligibility.tokenId);

                        return currentSelected || (eligibility.isEligible && availableForSelection);
                    })
                    .map((eligibility) => tokens[eligibility.tokenId]);

                const accaBoostToken = tokenList.filter(isAccaBoostToken).sort(byAccaPriority)[0];
                const crmTokens = tokenList
                    .filter((token) => !isAccaBoostToken(token))
                    .map((token) => this.extendWithSelectionInfo(token, selectedToken));

                return {
                    crmTokens: this.orderTokens(crmTokens),
                    accaBoostToken: accaBoostToken ? this.extendWithSelectionInfo(accaBoostToken, selectedToken) : undefined,
                };
            }),
        );

        this.store
            .select(selectIsLinearBetslip)
            .pipe(
                take(1),
                filter((isLinear) => isLinear),
            )
            .subscribe(() => this.store.dispatch(LinearBetslipTrackingActions.loadPromotionsPopup({ betslipType: this.tokenContext.betslipType })));
    }

    getTitle(model: TokensContainerViewModel): string {
        const tokens = model.crmTokens;
        let title = this.sitecore.crmTokens.messages.DrawerTitleGeneral;
        if (this.isRewardDrawerEnabled) {
            return title ? interpolateDoubleBracesString(title, { count: tokens.length.toString() }) : '';
        }

        if (model.accaBoostToken) {
            return this.sitecore.crmTokens.messages.PromotionsSectionTitle;
        }

        if (tokens.every((t) => t.token.rewardTokenType === RewardTokenType.FreeBet)) {
            title = this.sitecore.crmTokens.messages.DrawerTitleFreeBet;
        }

        if (tokens.every((t) => t.token.rewardTokenType === RewardTokenType.OddsBoost)) {
            title = this.sitecore.crmTokens.messages.DrawerTitleOddsBoost;
        }

        if (tokens.every((t) => t.token.rewardTokenType === RewardTokenType.RiskFreeBet)) {
            title = this.sitecore.crmTokens.messages.DrawerTitleRiskFree;
        }

        if (tokens.every((t) => t.token.rewardTokenType === RewardTokenType.BetAndGet)) {
            title = this.sitecore.crmTokens.messages.DrawerTitleBetAndGet;
        }

        return title ? interpolateDoubleBracesString(title, { count: tokens.length.toString() }) : '';
    }

    hasSelectedToken = (tokens: IRewardTokenInfo[]): boolean => tokens.some((token) => token.selected);

    closeDialog = () =>
        this.store.dispatch(
            RewardTokensActions.closeDialog({
                closeButtonClicked: true,
                tokenContext: this.tokenContext,
            }),
        );

    clearToken = ({ crmTokens, accaBoostToken }: TokensContainerViewModel) => {
        const selectedTokenInfo = accaBoostToken?.selected ? accaBoostToken : crmTokens.find((t) => t.selected);
        if (!selectedTokenInfo) {
            return;
        }

        this.store.dispatch(
            RewardTokensActions.closeDialog({
                tokenContext: this.tokenContext,
            }),
        );
        this.store.dispatch(
            RewardTokensActions.deselectToken({
                payload: {
                    tokenId: selectedTokenInfo.token.userTokenId,
                    pickId: this.tokenContext.pickId,
                    betslipType: this.tokenContext.betslipType,
                    trackingSource: this.trackingSource,
                },
            }),
        );
    };

    showClearButton(model: TokensContainerViewModel): boolean {
        return this.hasSelectedToken(model.crmTokens) && !model.accaBoostToken?.selected;
    }

    selectToken(tokenInfo: IRewardTokenInfo): void {
        this.store.dispatch(
            RewardTokensActions.toggleToken({
                payload: {
                    tokenId: tokenInfo.token.userTokenId,
                    pickId: this.tokenContext.pickId,
                    betslipType: this.tokenContext.betslipType,
                    trackingSource: this.trackingSource,
                },
            }),
        );
        this.isDoneButtonDisabled = false;
    }

    doneButtonClick = () => this.store.dispatch(RewardTokensActions.closeDialog());

    private extendWithSelectionInfo(token: IRewardToken, selectedToken: IRewardToken | null): IRewardTokenInfo {
        return {
            token,
            selected: token.userTokenId === selectedToken?.userTokenId,
        };
    }

    private orderTokens(tokenInfos: IRewardTokenInfo[]): IRewardTokenInfo[] {
        if (this.isTokenSortingEnabled) {
            // order first by selected, then by welcome offer, then by vip exclusive tokens w.r.t expiration date and then by rest of the tokens with expiration date
            return orderBy(tokenInfos, [
                (tokenInfo) => !tokenInfo.selected,
                (tokenInfo) => tokenInfo.token.rewardTargetType !== RewardTargetType.WelcomeOffer,
                (tokenInfo) => (this.tokensConfig.tokens.isVIPBadgeEnabled ? !tokenInfo.token.isVipExclusive && tokenInfo.token.expiryDate : false),
                (tokenInfo) => tokenInfo.token.expiryDate,
            ]);
        } else {
            // order first by selected, then by expiration date
            return orderBy(
                orderBy(tokenInfos, (tokenInfo) => tokenInfo.token.expiryDate, 'asc'),
                (tokenInfo) => tokenInfo.selected,
                'desc',
            );
        }
    }

    displayInfo(e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();

        this.store.dispatch(
            RewardTokensActions.showOverlayInfo({
                trackingSource: this.pickSourceProvider.getTracking(),
                tokenContext: this.tokenContext,
            }),
        );
    }
}

export function desktopMode(_: string, currentPosition: string): boolean {
    return currentPosition === RewardTokensContainerMode.Desktop.toString();
}

export function mobileMode(_: string, currentPosition: string): boolean {
    return currentPosition === RewardTokensContainerMode.Mobile.toString();
}
